import { useEffect, useState } from 'react'
import Modal from './Modal'
import { api, ApiClientError } from '@/lib/api'
import { toastError } from '@/store/toast'
import type { Customer, Gender } from '@yui-receipt/shared'

type Props = {
  open: boolean
  onClose: () => void
  onCustomer: (c: Customer) => void
  // When set, form opens in edit mode: CID is read-only, submit uses PATCH.
  customer?: Customer | null
}

type Form = {
  cid: string
  nameTh: string
  nameEn: string
  dob: string
  gender: Gender | ''
  address: string
  phone: string
}

const blank: Form = {
  cid: '',
  nameTh: '',
  nameEn: '',
  dob: '',
  gender: '',
  address: '',
  phone: '',
}

function fromCustomer(c: Customer): Form {
  return {
    cid: c.cidFull ?? '',
    nameTh: c.nameTh,
    nameEn: c.nameEn ?? '',
    dob: c.dob ?? '',
    gender: c.gender ?? '',
    address: c.address ?? '',
    phone: c.phone ?? '',
  }
}

export default function ManualCustomerModal({ open, onClose, onCustomer, customer }: Props) {
  const isEdit = customer != null
  const [form, setForm] = useState<Form>(isEdit ? fromCustomer(customer) : blank)
  const [loading, setLoading] = useState(false)

  // Sync form when switching between create/edit or loading new customer.
  useEffect(() => {
    if (open) setForm(isEdit && customer ? fromCustomer(customer) : blank)
  }, [open, customer, isEdit])

  function reset() {
    setForm(isEdit && customer ? fromCustomer(customer) : blank)
    setLoading(false)
  }

  async function submit() {
    if (!form.nameTh.trim()) {
      toastError('กรุณากรอกชื่อ-นามสกุล (ไทย)')
      return
    }

    const payload: Record<string, unknown> = {
      nameTh: form.nameTh.trim(),
      nameEn: form.nameEn.trim() || null,
      dob: form.dob || null,
      gender: form.gender || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
    }

    setLoading(true)
    try {
      let res: { customer: Customer }
      if (isEdit && customer) {
        res = await api<{ customer: Customer }>(`/customers/${customer.id}`, {
          method: 'PATCH',
          body: payload,
        })
      } else {
        const cid = form.cid.replace(/\D/g, '')
        if (cid.length !== 13) {
          toastError('CID ต้องเป็นตัวเลข 13 หลัก')
          setLoading(false)
          return
        }
        res = await api<{ customer: Customer }>('/customers/upsert', {
          body: { ...payload, cid },
        })
      }
      onCustomer(res.customer)
      reset()
      onClose()
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกคนไข้ไม่สำเร็จ'
      toastError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title={isEdit ? 'แก้ไขข้อมูลคนไข้' : 'กรอกข้อมูลคนไข้แบบแมนนวล'}
      widthClass="max-w-xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          {isEdit
            ? 'แก้ไขข้อมูลคนไข้ — เลขบัตรประชาชนแก้ไขไม่ได้ (ใช้เป็น key)'
            : 'กรอกข้อมูลจากบัตรประชาชนด้วยตัวเอง — ใช้เมื่อไม่มีเครื่องอ่านบัตรหรือรูปบัตร'}
        </p>
        <div>
          <label className="label">
            เลขประจำตัวประชาชน (13 หลัก) {!isEdit && <span className="text-rose-500">*</span>}
          </label>
          <input
            className="input font-mono disabled:bg-slate-100"
            value={form.cid}
            onChange={(e) =>
              setForm({ ...form, cid: e.target.value.replace(/\D/g, '').slice(0, 13) })
            }
            inputMode="numeric"
            placeholder="1234567890123"
            maxLength={13}
            autoFocus={!isEdit}
            disabled={isEdit}
          />
        </div>
        <div>
          <label className="label">
            ชื่อ-นามสกุล (ไทย) <span className="text-rose-500">*</span>
          </label>
          <input
            className="input"
            value={form.nameTh}
            onChange={(e) => setForm({ ...form, nameTh: e.target.value })}
            placeholder="นายสมชาย ใจดี"
            autoFocus={isEdit}
          />
        </div>
        <div>
          <label className="label">ชื่อ-นามสกุล (อังกฤษ)</label>
          <input
            className="input"
            value={form.nameEn}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
            placeholder="Mr. Somchai Jaidee"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">วันเกิด</label>
            <input
              className="input"
              type="date"
              value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
            />
          </div>
          <div>
            <label className="label">เพศ</label>
            <select
              className="input"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value as Gender | '' })}
            >
              <option value="">—</option>
              <option value="M">ชาย</option>
              <option value="F">หญิง</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">เบอร์โทรศัพท์</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            inputMode="tel"
            placeholder="081-234-5678"
          />
        </div>
        <div>
          <label className="label">ที่อยู่</label>
          <textarea
            className="input min-h-[80px]"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="เลขที่ หมู่ ถนน ตำบล อำเภอ จังหวัด รหัสไปรษณีย์"
          />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button
            className="btn-ghost"
            onClick={() => {
              reset()
              onClose()
            }}
            disabled={loading}
          >
            ยกเลิก
          </button>
          <button className="btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'กำลังบันทึก…' : isEdit ? 'บันทึกการแก้ไข' : 'บันทึก + เพิ่มคนไข้'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
